/**
 * The network: thirteen genre lines, and the rule that puts a recommendation on one.
 *
 * The concept pages hardcode nine lines of eight titles each. Real output is a
 * flat ranked list whose entries carry two to four AniList genres, so two things
 * have to be decided here: which line a title rides, and which lines exist at all
 * for a given rider.
 */

import type { Recommendation } from '../api/types';

export type LineSlug =
  | 'romance'
  | 'action'
  | 'fantasy'
  | 'comedy'
  | 'slice-of-life'
  | 'sci-fi'
  | 'mystery'
  | 'horror'
  | 'sports'
  | 'isekai'
  | 'mecha'
  | 'music'
  | 'supernatural';

export interface Line {
  slug: LineSlug;
  /**
   * Platform number. In `LINES` it is the railway's fixed order (the gate's
   * line-up); on a rider's network `buildNetwork` renumbers it to their
   * riding order.
   */
  id: string;
  /** Three-letter code printed on the ticket. */
  code: string;
  kanji: string;
  lineEn: string;
  genreEn: string;
  genreJp: string;
  /** The line's accent, for the hub board and the network map. */
  color: string;
  /** The same hue inked dark enough to read on the cream paper tickets. */
  ink: string;
}

export const LINES: Line[] = [
  { slug: 'romance', id: '01', code: 'SKR', kanji: '桜線', lineEn: 'SAKURA LINE', genreEn: 'ROMANCE', genreJp: '恋愛', color: '#FF4F9B', ink: '#D4196A' },
  { slug: 'action', id: '02', code: 'RPD', kanji: '疾風線', lineEn: 'RAPID LINE', genreEn: 'ACTION', genreJp: '格闘', color: '#FF2D3F', ink: '#D0121F' },
  { slug: 'fantasy', id: '03', code: 'AET', kanji: '幻想線', lineEn: 'AETHER LINE', genreEn: 'FANTASY', genreJp: '幻想', color: '#E8B450', ink: '#8F6516' },
  { slug: 'comedy', id: '04', code: 'POP', kanji: '爆笑線', lineEn: 'POP LINE', genreEn: 'COMEDY', genreJp: '喜劇', color: '#FFD11A', ink: '#806600' },
  { slug: 'slice-of-life', id: '05', code: 'LCL', kanji: '日常線', lineEn: 'LOCAL LINE', genreEn: 'SLICE OF LIFE', genreJp: '日常', color: '#8FA870', ink: '#5E7445' },
  { slug: 'sci-fi', id: '06', code: 'NEO', kanji: '電脳線', lineEn: 'NEON LINE', genreEn: 'SCI-FI', genreJp: '電脳', color: '#34E5FF', ink: '#00788A' },
  { slug: 'mystery', id: '07', code: 'NOR', kanji: '推理線', lineEn: 'NOIR LINE', genreEn: 'MYSTERY', genreJp: '推理', color: '#E0A83C', ink: '#8C6418' },
  { slug: 'horror', id: '08', code: 'DRD', kanji: '戦慄線', lineEn: 'DREAD LINE', genreEn: 'HORROR', genreJp: '戦慄', color: '#C4162E', ink: '#C4162E' },
  { slug: 'sports', id: '09', code: 'RLY', kanji: '熱血線', lineEn: 'RALLY LINE', genreEn: 'SPORTS', genreJp: '熱血', color: '#FF6A1F', ink: '#BF4300' },
  // Split out of the first nine, each where one line was carrying two kinds of
  // show that look and feel nothing alike: isekai and mecha out of fantasy and
  // sci-fi, music out of slice of life, the youkai-and-shrines supernatural out
  // of fantasy.
  { slug: 'isekai', id: '10', code: 'ISK', kanji: '異界線', lineEn: 'PORTAL LINE', genreEn: 'ISEKAI', genreJp: '異世界', color: '#2FD08A', ink: '#1B7D53' },
  { slug: 'mecha', id: '11', code: 'MCH', kanji: '機甲線', lineEn: 'IRON LINE', genreEn: 'MECHA', genreJp: '機甲', color: '#9AA8BA', ink: '#5E708A' },
  { slug: 'music', id: '12', code: 'ENC', kanji: '響線', lineEn: 'ENCORE LINE', genreEn: 'MUSIC', genreJp: '音楽', color: '#B266FF', ink: '#8A3FD9' },
  { slug: 'supernatural', id: '13', code: 'LTN', kanji: '妖線', lineEn: 'LANTERN LINE', genreEn: 'SUPERNATURAL', genreJp: '怪異', color: '#6EA8FF', ink: '#2B63C8' },
];

export const LINE_BY_SLUG: Record<LineSlug, Line> = Object.fromEntries(
  LINES.map((l) => [l.slug, l]),
) as Record<LineSlug, Line>;

/**
 * Which line a title rides is the one it most looks like, weighing everything
 * AniList says about it. Genres alone cannot answer that: they come
 * alphabetically, carry no weight, and most titles have three or four, so any
 * fixed priority order buries whichever genre it puts last (it used to be
 * Action, and the RAPID LINE almost never ran). Tags can: each comes with a
 * rank, 0–100, for how central AniList's voters think it is, so `Attack on
 * Titan`'s Military 88 and Kaiju 93 outvote its Fantasy genre, and `Spy x
 * Family`'s Espionage 95 is weighed against its Family Life 95.
 *
 * Each line scores the sum of its evidence and the highest score wins:
 *  - a genre counts its weight below. The weight is how much the genre alone
 *    says — Sports or Mecha nearly settle it, Drama or Adventure hardly do. A
 *    line's strongest genre counts in full and the rest at half, so Mystery +
 *    Thriller + Psychological is one strong vote, not three.
 *  - a tag counts its weight scaled by its rank: 50 adds a sixth of it, 70
 *    half, 100 all of it. Tags below 50 never reach the frontend.
 */
const GENRE_WEIGHTS: Record<string, [LineSlug, number]> = {
  Sports: ['sports', 1.6],
  Horror: ['horror', 1.8],
  Mystery: ['mystery', 1.1],
  Thriller: ['mystery', 0.9],
  Psychological: ['mystery', 0.9],
  Romance: ['romance', 1.7],
  'Sci-Fi': ['sci-fi', 1.0],
  Mecha: ['mecha', 2.2],
  Fantasy: ['fantasy', 1.0],
  Supernatural: ['supernatural', 1.0],
  'Mahou Shoujo': ['fantasy', 1.4],
  Adventure: ['fantasy', 0.5],
  'Slice of Life': ['slice-of-life', 0.9],
  Music: ['music', 2.0],
  Drama: ['slice-of-life', 0.3],
  Comedy: ['comedy', 0.9],
  Ecchi: ['comedy', 0.5],
  Action: ['action', 0.9],
};

/**
 * The tags that say which line a title belongs on, out of AniList's ~400. The
 * rest (cast traits, demographics, technical notes) say nothing about it and
 * are ignored. Weight 1 is a tag that is the line's own subject; lower weights
 * lean that way without settling it (`School` is where half of all romances
 * and a good many horrors happen).
 */
const TAG_WEIGHTS: Record<string, [LineSlug, number]> = {};
function tags(line: LineSlug, weight: number, names: string[]) {
  for (const name of names) TAG_WEIGHTS[name] = [line, weight];
}
tags('action', 1, [
  'Archery', 'Espionage', 'Fugitive', 'Guns', 'Martial Arts', 'Spearplay', 'Swordplay',
  'Superhero', 'Super Power', 'Samurai', 'Ninja', 'Assassins', 'Military', 'War', 'Proxy Battle', 'Gangs',
  'Delinquents', 'Wuxia',
]);
tags('action', 0.5, ['Shounen', 'Revenge', 'Yakuza', 'Mafia', 'Triads', 'Kaiju', 'Tanks']);
tags('fantasy', 1, [
  'Alchemy', 'Body Swapping', 'Cultivation', 'Fairy Tale', 'Henshin', 'Magic', 'Mythology', 'Necromancy',
  'Dragons', 'Elf', 'Fairy', 'Angels', 'Witch', 'Medieval', 'Dungeon', 'Prophecy', 'Lost Civilization',
  'Creature Taming', 'Kingdom Management',
]);
tags('fantasy', 0.6, ['Steampunk', 'Royal Affairs', 'Demons']);
// Isekai is mostly decided by PREMISE_TAGS below; these count when it is not central.
tags('isekai', 1.5, ['Isekai', 'Reverse Isekai']);
tags('isekai', 0.8, ['Reincarnation']);
tags('supernatural', 1, ['Youkai', 'Exorcism', 'Curses', 'Gods', 'Shapeshifting', 'Afterlife', 'Shrine Maiden']);
tags('supernatural', 0.6, ['Urban Fantasy']);
tags('mecha', 1.5, ['Real Robot', 'Super Robot']);
tags('music', 1.2, [
  'Band', 'Classical Music', 'Dancing', 'Hip-hop Music', 'Jazz Music', 'Metal Music', 'Musical Theater', 'Rock Music',
  'Idol',
]);
tags('sci-fi', 1, [
  'Cyberpunk', 'Space Opera', 'Time Loop', 'Time Manipulation', 'Tokusatsu', 'Space',
  'Aliens', 'Artificial Intelligence', 'Cyborg', 'Robots', 'Clone', 'Virtual World', 'Augmented Reality',
  'Post-Apocalyptic', 'Human Experimentation', 'Astronomy', 'Software Development',
]);
tags('sci-fi', 0.6, ['Dystopian', 'Video Games']);
tags('mystery', 1, [
  'Detective', 'Crime', 'Noir', 'Conspiracy', 'Police', 'Blackmail', 'Death Game', 'Memory Manipulation',
  'Terrorism', 'Brainwashing', 'Amnesia',
]);
tags('mystery', 0.6, ['Criminal Organization', 'Gambling', 'Dissociative Identities', 'Battle Royale']);
tags('horror', 1, [
  'Body Horror', 'Cosmic Horror', 'Gore', 'Eco-Horror', 'Zombie', 'Vampire', 'Ghost', 'Cannibalism', 'Ero Guro',
  'Torture', 'Pandemic', 'Cult',
]);
tags('horror', 0.5, ['Survival', 'Suicide']);
tags('romance', 1, [
  'Age Gap', "Boys' Love", 'Cheating', 'Cohabitation', 'Female Harem', 'Interspecies', 'Love Triangle', 'Male Harem',
  'Matchmaking', 'Mixed Gender Harem', 'Polyamorous', "Teens' Love", 'Unrequited Love', 'Yuri', 'Marriage',
  'Arranged Marriage', 'Fake Relationship',
]);
tags('romance', 0.5, ['Heterosexual', 'Tsundere', 'Yandere']);
tags('comedy', 1, ['Parody', 'Satire', 'Slapstick', 'Surreal Comedy', 'Manzai', 'Chibi', '4-koma']);
tags('slice-of-life', 1, [
  'Agriculture', 'Cute Boys Doing Cute Things', 'Cute Girls Doing Cute Things', 'Family Life', 'Horticulture',
  'Iyashikei', 'Parenthood', 'Food', 'Camping', 'Work', 'Office', 'Konbini', 'Restaurant', 'Inn', 'Acting', 'Drawing', 'Writing', 'Fashion', 'Photography', 'Rakugo', 'Calligraphy', 'Filmmaking',
  'Otaku Culture', 'Rural', 'Rehabilitation',
]);
tags('slice-of-life', 0.5, ['School Club', 'Travel']);
tags('slice-of-life', 0.3, ['Episodic', 'School', 'Coming of Age', 'Found Family']);
tags('sports', 1, [
  'Acrobatics', 'Airsoft', 'American Football', 'Athletics', 'Badminton', 'Baseball', 'Basketball', 'Bowling',
  'Boxing', 'Cheerleading', 'Cycling', 'Fencing', 'Fishing', 'Fitness', 'Football', 'Golf', 'Handball',
  'Ice Sports', 'Judo', 'Lacrosse', 'Parkour', 'Rugby', 'Scuba Diving', 'Skateboarding', 'Sumo', 'Surfing',
  'Swimming', 'Table Tennis', 'Tennis', 'Volleyball', 'Wrestling', 'E-Sports', 'Go', 'Shogi', 'Karuta', 'Mahjong',
  'Card Battle', 'Poker', 'Board Game', 'Mountaineering',
]);

/**
 * Anything with no evidence at all — no genres or tags this file knows — rides
 * the LOCAL LINE. It is the general-service line in the concept's own fiction,
 * and putting strays there beats inventing a tenth platform for them.
 */
const FALLBACK: LineSlug = 'slice-of-life';

/** Every line's evidence for this title. Exported for tests. */
export function lineScores(rec: Recommendation): Map<LineSlug, number> {
  const scores = new Map<LineSlug, number>();
  const add = (line: LineSlug, v: number) => scores.set(line, (scores.get(line) ?? 0) + v);

  const byLine = new Map<LineSlug, number[]>();
  for (const genre of new Set(rec.genres)) {
    const hit = GENRE_WEIGHTS[genre];
    if (hit) byLine.set(hit[0], [...(byLine.get(hit[0]) ?? []), hit[1]]);
  }
  for (const [line, weights] of byLine) {
    const [strongest = 0, ...rest] = weights.sort((a, b) => b - a);
    add(line, strongest + rest.reduce((sum, w) => sum + w / 2, 0));
  }

  // `?? []`: tolerate a backend from before tags were part of the contract.
  for (const tag of rec.tags ?? []) {
    const hit = Object.hasOwn(TAG_WEIGHTS, tag.name) ? TAG_WEIGHTS[tag.name] : undefined;
    if (hit && tag.rank >= 50) add(hit[0], (hit[1] * (tag.rank - 40)) / 60);
  }
  return scores;
}

/**
 * A few tags name a show's premise rather than a flavour of it. An isekai is
 * nearly always Fantasy + Adventure with a pile of fantasy tags besides (Magic,
 * Medieval, Dungeon, ...), which additive scoring would always let outvote the
 * one tag that makes it an isekai. When a premise tag is central — ranked at
 * least this high — the title rides that line outright.
 */
const PREMISE_TAGS: Record<string, [LineSlug, number]> = {
  Isekai: ['isekai', 70],
  'Reverse Isekai': ['isekai', 70],
};

/** The line an AniList genre name rides, or null for genres no line is built around. */
export function lineForGenre(genre: string): LineSlug | null {
  return Object.hasOwn(GENRE_WEIGHTS, genre) ? GENRE_WEIGHTS[genre]![0] : null;
}

export function lineFor(rec: Recommendation): LineSlug {
  for (const tag of rec.tags ?? []) {
    const premise = Object.hasOwn(PREMISE_TAGS, tag.name) ? PREMISE_TAGS[tag.name] : undefined;
    if (premise && tag.rank >= premise[1]) return premise[0];
  }
  const scores = lineScores(rec);
  let best: LineSlug = FALLBACK;
  let top = 0;
  // Platform order breaks exact ties, so the answer never depends on the
  // order AniList happened to list things in.
  for (const line of LINES) {
    const score = scores.get(line.slug) ?? 0;
    if (score > top) {
      best = line.slug;
      top = score;
    }
  }
  return best;
}

export interface StationStop {
  line: Line;
  picks: Recommendation[];
}

/**
 * The line a genre names outright, or null for the broad ones (Drama,
 * Adventure, Ecchi) that sit on most of a list without saying much about it:
 * a list that is 60% Drama has not told us it wants the LOCAL LINE.
 */
export function lineNamedBy(genre: string): LineSlug | null {
  const hit = Object.hasOwn(GENRE_WEIGHTS, genre) ? GENRE_WEIGHTS[genre]! : null;
  return hit && hit[1] >= 0.9 ? hit[0] : null;
}

/**
 * How much of the rider's own list each line stands for, 0–1, from the intro's
 * genre breakdown (share of the list carrying each AniList genre). A line takes
 * its best genre's share: Mystery 40% and Thriller 30% is a 40% NOIR LINE, not
 * 70%, since the two mostly sit on the same titles.
 */
export function lineAffinity(breakdown: { genre: string; share: number }[]): Map<LineSlug, number> {
  const out = new Map<LineSlug, number>();
  for (const { genre, share } of breakdown) {
    const slug = lineNamedBy(genre);
    if (slug && share > (out.get(slug) ?? 0)) out.set(slug, share);
  }
  return out;
}

/**
 * The rider's own network: only the lines their recommendations actually reach,
 * in the order this rider should ride them. Each line keeps its titles in rank
 * order.
 *
 * The order is the rider's, not the railway's: lines for the genres their own
 * list leans on most come first (`affinity`, when the intro's numbers are in),
 * then the rest by where their strongest recommendation sits. Platforms are
 * numbered in that order, so the board reads 01, 02, 03 down the page and
 * "start at platform 01" is always the top of it.
 */
export function buildNetwork(
  recommendations: Recommendation[],
  affinity: Map<LineSlug, number> = new Map(),
): StationStop[] {
  const byLine = new Map<LineSlug, Recommendation[]>();
  for (const rec of recommendations) {
    const slug = lineFor(rec);
    const bucket = byLine.get(slug);
    if (bucket) bucket.push(rec);
    else byLine.set(slug, [rec]);
  }
  const best = (slug: LineSlug) => Math.min(...byLine.get(slug)!.map((r) => r.rank));
  return LINES.filter((line) => byLine.has(line.slug))
    .map((line, platform) => ({ line, platform }))
    .sort(
      (a, b) =>
        (affinity.get(b.line.slug) ?? 0) - (affinity.get(a.line.slug) ?? 0) ||
        best(a.line.slug) - best(b.line.slug) ||
        // Fixed platform order breaks what is left, so the answer never
        // depends on the order the recommendations came in.
        a.platform - b.platform,
    )
    .map(({ line }, i) => ({
      line: { ...line, id: String(i + 1).padStart(2, '0') },
      picks: byLine.get(line.slug)!,
    }));
}

/** Next / previous stop on the rider's network, wrapping at both ends. */
export function neighbours(network: StationStop[], slug: LineSlug) {
  const i = network.findIndex((s) => s.line.slug === slug);
  if (i === -1) return { index: -1, prev: null, next: null };
  const n = network.length;
  return {
    index: i,
    prev: n > 1 ? network[(i - 1 + n) % n] : null,
    next: n > 1 ? network[(i + 1) % n] : null,
  };
}
